import {
  AllowedJobTypeList,
  type CreatedByCategory,
  type ExtensionPrompt,
  type Group,
  type Metadata,
  type Prompt,
} from "@1js/pl-types";
import {
  isExtensionPrompt,
  isMicrosoftOrigin,
  isMicrosoftPrompt,
  isNonSharedUserPrompt,
  isTenantPrompt,
  isUserPrompt,
  isWorkgroupPrompt,
} from "@1js/pl-utils";
import { Avatar } from "@fluentui/react-components";
import * as React from "react";
import { JobTypeWeights, type JobType } from "../data";
import {
  CreatedByKey,
  DisplayCategoryKey,
  ExtensionKey,
  JobTypeKey,
  LikedKey,
  ProductNameKey,
  RoleKey,
  WorkgroupKey,
} from "../PromptLibrary/PromptLibrary.keys";
import {
  FILTER_CONCATENATE_TOKEN,
  type Filter,
  type FilterName,
  type FilterOption,
  type NestedFilter,
  type OrganizationType,
  type SubMenuGroup,
} from "./types";

export const groupFilterOptions = (filterOptions: FilterOption[]) => {
  return filterOptions.reduce(
    (
      acc: {
        [group: string]: {
          value: string;
          group?: string;
        }[];
      },
      curr: {
        value: string;
        group?: string;
      }
    ) => {
      const group = curr.group ?? "NA";
      if (!acc[group]) {
        acc[group] = [];
      }
      acc[group].push(curr);
      return acc;
    },
    {}
  );
};
export const constructNestedOptions = (
  options: FilterOption[]
): NestedFilter[] => {
  const groupedOptions = groupFilterOptions(options);
  const nestedOptions = Object.keys(groupedOptions).map((groupKey) => {
    return {
      displayLabel: groupKey,
      options: groupedOptions[groupKey],
    };
  });
  return nestedOptions;
};

const disableSubMenuOptions = (
  subMenuGroup: SubMenuGroup,
  selectedValues: Record<string, string[]>
): SubMenuGroup => {
  const jobTypeValues = selectedValues[JobTypeKey] || [];
  const roleValues = selectedValues[RoleKey] || [];
  const createdByValues = selectedValues[CreatedByKey] || [];

  const jobTypeGroups = new Set<string>();
  jobTypeValues.forEach((jobTypeFilterValue) => {
    const jobTypeFilterValueSplit = jobTypeFilterValue.split(
      FILTER_CONCATENATE_TOKEN
    );
    const jobTypeFilterValueGroup = jobTypeFilterValueSplit[1];
    jobTypeGroups.add(jobTypeFilterValueGroup);
  });

  const roleGroups = new Set<string>();
  roleValues.forEach((roleFilterValue) => {
    const roleFilterValueSplit = roleFilterValue.split(
      FILTER_CONCATENATE_TOKEN
    );
    const roleFilterValueGroup = roleFilterValueSplit[1];
    roleGroups.add(roleFilterValueGroup);
  });

  const modifiedGroup = { ...subMenuGroup };
  switch (subMenuGroup.groupLabel as OrganizationType) {
    case "FromMicrosoft":
      modifiedGroup.disabled =
        (jobTypeGroups.size > 0 && !jobTypeGroups.has("FromMicrosoft")) ||
        (roleGroups.size > 0 && !roleGroups.has("FromMicrosoft")) ||
        (createdByValues.length > 0 &&
          !createdByValues.includes(
            "CreatedByMicrosoft" as CreatedByCategory
          ) &&
          !createdByValues.includes("CreatedByAll" as CreatedByCategory));
      break;
    case "FromOrganization":
      modifiedGroup.disabled =
        (jobTypeGroups.size > 0 && !jobTypeGroups.has("FromOrganization")) ||
        (roleGroups.size > 0 && !roleGroups.has("FromOrganization")) ||
        (createdByValues.length > 0 &&
          !createdByValues.includes("CreatedByTenant" as CreatedByCategory) &&
          !createdByValues.includes("CreatedByAll" as CreatedByCategory));

      break;
  }
  return modifiedGroup;
};

type BaseFilterWrapperConstructorProps = {
  isDisabled?: boolean;
};
export abstract class BaseFilterWrapper {
  private isDisabled = false;
  options: FilterOption[];
  submenuGroups?: SubMenuGroup[];
  constructor({ isDisabled }: BaseFilterWrapperConstructorProps) {
    this.isDisabled = !!isDisabled;
    this.options = [];
  }
  abstract ParsePrompt(prompt: Prompt): void;

  abstract GetFilter(
    selectedFilterValues?: Record<FilterName, string[]>,
    prompts?: Prompt[]
  ): Filter;

  get IsDisabled(): boolean {
    return this.isDisabled;
  }

  get Options(): FilterOption[] {
    return this.options;
  }

  set Options(options: FilterOption[]) {
    this.options = options;
    this.setOptionListener(options);
  }

  private setOptionListener = (_options: FilterOption[]) => {
    return;
  };

  registerOptionListener = (listener: (options: FilterOption[]) => void) => {
    this.setOptionListener = listener;
  };

  abstract DisableOptions(
    selectedFilterValues: Record<FilterName, string[]>
  ): void;
}

export class DisplayCategoryFilterWrapper extends BaseFilterWrapper {
  private displayCategoriesValues: Set<string> = new Set<string>();
  private displayCategoryCounts: Record<string, number> = {};

  ParsePrompt(prompt: Prompt): void {
    if (
      !isUserPrompt(prompt) &&
      prompt.DisplayCategory &&
      prompt.DisplayCategory !== "Agent"
    ) {
      this.displayCategoriesValues.add(prompt.DisplayCategory.toString());
      if (
        !this.options.find((option) => option.value === prompt.DisplayCategory)
      ) {
        this.options.push({
          value: prompt.DisplayCategory,
        });
      }
      if (this.displayCategoryCounts[prompt.DisplayCategory]) {
        this.displayCategoryCounts[prompt.DisplayCategory] += 1;
      } else {
        this.displayCategoryCounts[prompt.DisplayCategory] = 1;
      }
    }
  }
  GetFilter(
    _selectedFilterValues?: Record<FilterName, string[]>,
    prompts?: Prompt[]
  ): Filter {
    return {
      key: DisplayCategoryKey,
      options:
        this.options.length > 1
          ? this.options
          : this.options.filter((option) => {
              return prompts
                ? this.displayCategoryCounts[option.value] !== prompts.length
                : true;
            }),
      type: "dropdown",
      isLogValuesInTelemetry: true,
    };
  }

  DisableOptions(_selectedFilterValues: Record<FilterName, string[]>): void {
    // No implementation needed for DisplayCategory filter
    return;
  }
}

export class ProductNameFilterWrapper extends BaseFilterWrapper {
  private productNameValues: string[] = new Array<string>();
  private productCounts: Record<string, number> = {};
  ParsePrompt(prompt: Prompt): void {
    if (!isUserPrompt(prompt) && prompt.Products) {
      this.productNameValues.push(...prompt.Products);
      prompt.Products.forEach((productName) => {
        if (!this.options.find((option) => option.value === productName)) {
          this.options.push({
            value: productName,
          });
        }
        if (this.productCounts[productName]) {
          this.productCounts[productName] += 1;
        } else {
          this.productCounts[productName] = 1;
        }
      });
    }
  }
  GetFilter(
    _selectedFilterValues?: Record<FilterName, string[]>,
    prompts?: Prompt[]
  ): Filter {
    return {
      key: ProductNameKey,
      options:
        this.options.length > 1
          ? this.options
          : this.options.filter((option) => {
              return prompts
                ? this.productCounts[option.value] !== prompts.length
                : true;
            }),
      type: "dropdown",
      isLogValuesInTelemetry: true,
    };
  }
  DisableOptions(_selectedFilterValues: Record<FilterName, string[]>): void {
    return;
  }
}

export class LikedFilterWrapper extends BaseFilterWrapper {
  ParsePrompt(_prompt: Prompt): void {
    // No implementation needed for Liked filter
  }

  GetFilter(): Filter {
    return {
      key: LikedKey,
      options: [{ value: "Liked" }, { value: "NotLiked" }],
      isSingleSelect: true,
      hidden: true,
      type: "dropdown",
      isLogValuesInTelemetry: true,
    };
  }

  DisableOptions(_selectedFilterValues: Record<FilterName, string[]>): void {
    return;
  }
}

type JobTypeFilterWrapperConstructorProps =
  BaseFilterWrapperConstructorProps & {
    enableNestedOptions?: boolean;
  };
export class JobTypeFilterWrapper extends BaseFilterWrapper {
  private microsoftMetadata: Metadata[] = [];
  private tenantMetadata: Metadata[] = [];

  private isNested = false;

  constructor({
    enableNestedOptions,
    isDisabled,
  }: JobTypeFilterWrapperConstructorProps) {
    super({ isDisabled });
    this.isNested = !!enableNestedOptions;
    // Whenever the options are set, we update the submenu groups accordingly
    this.registerOptionListener((_options: FilterOption[]) => {
      this.submenuGroups = [
        ...(this.tenantMetadata.length > 0
          ? [
              {
                groupLabel: "FromOrganization" as OrganizationType,
                nestedOptions: constructNestedOptions(
                  this.convertMetadataToJobTypeFilters(this.tenantMetadata)
                ) as NestedFilter[],
              },
            ]
          : []),
        ...(this.microsoftMetadata.length > 0
          ? [
              {
                groupLabel: "FromMicrosoft" as OrganizationType,
                nestedOptions: constructNestedOptions(
                  this.convertMetadataToJobTypeFilters(this.microsoftMetadata)
                ) as NestedFilter[],
              },
            ]
          : []),
      ] as SubMenuGroup[];
    });
  }

  private convertMetadataToJobTypeFilters = (metadatas: Metadata[]) => {
    const uniqueJobTypeFilterTracker = new Set<string>();
    let metadataFilters: FilterOption[] = [];
    metadatas.forEach((item: Metadata) => {
      if (AllowedJobTypeList.has(item.Name)) {
        const key = `${item.Name}_${item.Value}`;
        if (!uniqueJobTypeFilterTracker.has(key)) {
          uniqueJobTypeFilterTracker.add(key);
          metadataFilters.push({
            value: item.Value,
            group: item.Name,
          });
        }
      }
    });

    metadataFilters = metadataFilters.sort((a, b) => {
      const aWeight = JobTypeWeights[a.group as JobType] || 0;
      const bWeight = JobTypeWeights[b.group as JobType] || 0;
      return aWeight - bWeight;
    });

    return metadataFilters;
  };

  ParsePrompt(prompt: Prompt): void {
    if (prompt.Metadata) {
      if (isTenantPrompt(prompt)) {
        this.tenantMetadata = [...this.tenantMetadata, ...prompt.Metadata];
      } else {
        this.microsoftMetadata = [
          ...this.microsoftMetadata,
          ...prompt.Metadata,
        ];
      }
      this.Options = this.convertMetadataToJobTypeFilters([
        ...this.microsoftMetadata,
        ...this.tenantMetadata,
      ]);
    }
  }

  GetFilter(selectedFilterValues?: Record<FilterName, string[]>): Filter {
    // testing purposes only, to be removed
    let submenuGroups = this.submenuGroups || [];
    if (selectedFilterValues) {
      submenuGroups = submenuGroups.map((group) =>
        disableSubMenuOptions(group, selectedFilterValues)
      );
    }
    return {
      key: JobTypeKey,
      options: this.options,
      type: this.isNested ? "nested" : "dropdown",
      isLogValuesInTelemetry: true,
      ...(this.isNested
        ? {
            submenuGroups,
          }
        : {}),
    };
  }

  DisableOptions(_selectedFilterValues: Record<FilterName, string[]>): void {
    return;
  }
}

export class CreatedByFilterWrapper extends BaseFilterWrapper {
  private createdByCounts: Record<string, number> = {};
  private isRocksteadyUxEnabled: boolean;
  constructor({
    isDisabled,
    isRocksteadyUxEnabled,
  }: BaseFilterWrapperConstructorProps & { isRocksteadyUxEnabled?: boolean }) {
    super({ isDisabled });
    this.isRocksteadyUxEnabled = !!isRocksteadyUxEnabled;
  }

  ParsePrompt(prompt: Prompt): void {
    let value = "";
    if (isTenantPrompt(prompt)) {
      value = "CreatedByTenant";
    } else if (isWorkgroupPrompt(prompt)) {
      value = "CreatedByOthers";
    } else if (isMicrosoftOrigin(prompt) || isMicrosoftPrompt(prompt)) {
      value = "CreatedByMicrosoft";
    }

    if (!this.isRocksteadyUxEnabled) {
      if (
        (prompt.Origin === "User" && !isWorkgroupPrompt(prompt)) ||
        prompt.UserActivity?.Favorite?.IsFavorite
      ) {
        value = "CreatedByMe";
      }
    } else if (isNonSharedUserPrompt(prompt)) {
      value = "CreatedByMe";
    }

    if (this.createdByCounts[value]) {
      this.createdByCounts[value] += 1;
    } else {
      this.createdByCounts[value] = 1;
    }
  }
  GetFilter(
    selectedFilterValues?: Record<FilterName, string[]>,
    prompts?: Prompt[]
  ): Filter {
    let options = Object.keys(this.createdByCounts).map((key) => ({
      value: key,
    }));
    if (selectedFilterValues) {
      options = options.map((option) => {
        return CreatedByFilterWrapper.DisableMenuOptions(
          option,
          selectedFilterValues
        );
      });
    }

    return {
      key: CreatedByKey,
      isSingleSelect: true,
      options:
        options.length > 1
          ? options
          : options.filter((option) => {
              return prompts
                ? this.createdByCounts[option.value] !== prompts.length
                : true;
            }),
      type: "dropdown",
    };
  }

  DisableOptions(_selectedFilterValues: Record<FilterName, string[]>): void {
    return;
  }
  static DisableMenuOptions = (
    createdByOption: FilterOption,
    selectedValues: Record<string, string[]>
  ): FilterOption => {
    const jobTypeFilterValues = (selectedValues[JobTypeKey] as string[]) || [];
    const roleFilterValues = selectedValues[RoleKey] || [];
    const createdByFilterValues = selectedValues[CreatedByKey] || [];

    if (jobTypeFilterValues.length < 1 && roleFilterValues.length < 1) {
      return createdByOption;
    }

    const modifiedOption = { ...createdByOption };
    const jobTypeGroups = new Set<string>();
    jobTypeFilterValues.forEach((jobTypeFilterValue) => {
      const jobTypeFilterValueSplit = jobTypeFilterValue.split(
        FILTER_CONCATENATE_TOKEN
      );
      if (jobTypeFilterValueSplit.length < 3) {
        return modifiedOption;
      }
      const jobTypeFilterValueGroup = jobTypeFilterValueSplit[1];
      jobTypeGroups.add(jobTypeFilterValueGroup);
    });

    const roleGroups = new Set<string>();
    roleFilterValues.forEach((roleFilterValue) => {
      const roleFilterValueSplit = roleFilterValue.split(
        FILTER_CONCATENATE_TOKEN
      );
      const roleFilterValueGroup = roleFilterValueSplit[1];
      roleGroups.add(roleFilterValueGroup);
    });
    switch (modifiedOption.value) {
      case "CreatedByMicrosoft":
        modifiedOption.disabled =
          !createdByFilterValues.includes("CreatedByMicrosoft") &&
          ((jobTypeGroups.size > 0 && !jobTypeGroups.has("FromMicrosoft")) ||
            (roleGroups.size > 0 && !roleGroups.has("FromMicrosoft")));
        break;
      case "CreatedByTenant":
        modifiedOption.disabled =
          !createdByFilterValues.includes("CreatedByTenant") &&
          ((jobTypeGroups.size > 0 && !jobTypeGroups.has("FromOrganization")) ||
            (roleGroups.size > 0 && !roleGroups.has("FromOrganization")));
        break;
      default:
        modifiedOption.disabled = true;
    }
    return modifiedOption;
  };
}
export type ExtensionPartyType = {
  Origin: string;
  ProviderPartyType: string;
  ExtensionLoggableId?: string;
};
type ExtensionFilterWrapperConstructorProps =
  BaseFilterWrapperConstructorProps & {
    allPrompts: Prompt[];
    extensionIdMap?: Record<string, string>;
    createTelemetryValueMap: (
      extensions: Record<string, ExtensionPartyType>
    ) => Record<string, string>;
  };

export class ExtensionFilterWrapper extends BaseFilterWrapper {
  private allPrompts: Prompt[] = [];
  private extensionIdMap: Record<string, string> | undefined;
  private extensions: Record<string, ExtensionPartyType>;
  private createTelemetryValueMap: (
    extensions: Record<string, ExtensionPartyType>
  ) => Record<string, string>;

  constructor({
    allPrompts,
    isDisabled,
    extensionIdMap,
    createTelemetryValueMap,
  }: ExtensionFilterWrapperConstructorProps) {
    super({ isDisabled });
    this.allPrompts = allPrompts;
    this.extensionIdMap = extensionIdMap;
    this.extensions = {};
    this.createTelemetryValueMap = createTelemetryValueMap;
  }

  /**
   * Filters out extensions if they are present in all prompts
   * @param extensions Record of extension name and associated extension and party type
   * @param prompts list of prompts
   * @returns Filtered record of extension name and associated extension and party type
   */
  private postProcessExtensionFilters = (
    extensions: Record<string, ExtensionPartyType>,
    prompts: Prompt[]
  ): Record<string, ExtensionPartyType> => {
    const filteredExtensions: Record<string, ExtensionPartyType> = {};

    Object.entries(extensions).forEach(
      ([extensionName, extensionPartyType]) => {
        if (!prompts.every((prompt) => prompt.ProviderName === extensionName)) {
          filteredExtensions[extensionName] = extensionPartyType;
        }
      }
    );

    return filteredExtensions;
  };

  ParsePrompt(prompt: Prompt): void {
    if (prompt.Origin && prompt.ProviderName && isExtensionPrompt(prompt)) {
      const extensionPrompt = prompt as ExtensionPrompt;
      const extensionId = extensionPrompt.ProviderId
        ? this.extensionIdMap?.[extensionPrompt.ProviderId]
        : undefined;

      this.extensions[prompt.ProviderName] = {
        Origin: prompt.Origin,
        ProviderPartyType: (prompt as ExtensionPrompt)
          .ProviderPartyType as string,
        ExtensionLoggableId: extensionId,
      };
    }
  }

  GetFilter(): Filter {
    this.extensions = this.postProcessExtensionFilters(
      this.extensions,
      this.allPrompts
    );
    return {
      key: ExtensionKey,
      options: [...new Set(Object.keys(this.extensions))]
        .map((x) => {
          return { value: x };
        })
        .sort((a, b) => (a.value > b.value ? 1 : -1)),
      type: "dropdown",
      isLogValuesInTelemetry: false,
      telemetryValueOverrides: this.createTelemetryValueMap(this.extensions),
    };
  }

  DisableOptions(_selectedFilterValues: Record<FilterName, string[]>): void {
    return;
  }
}

type WorkgroupFilterWrapperConstructorProps =
  BaseFilterWrapperConstructorProps & {
    groups: Group[];
  };
export class WorkgroupFilterWrapper extends BaseFilterWrapper {
  private workgroupIds = new Set<string>();
  private groups: Group[];
  constructor({ groups, isDisabled }: WorkgroupFilterWrapperConstructorProps) {
    super({ isDisabled });
    this.groups = groups;
  }

  ParsePrompt(prompt: Prompt): void {
    // No implementation needed for Workgroup filter
    if (prompt.Groups) {
      prompt.Groups.forEach((group) => {
        this.workgroupIds.add(group.Id);
      });
    }
  }

  GetFilter(): Filter {
    return {
      key: WorkgroupKey,
      options: this.groups
        .filter((group) => this.workgroupIds.has(group.Id))
        .map((group) => {
          const image = group.AvatarUrl ? { src: group.AvatarUrl } : undefined;
          return {
            value: group.Id,
            displayLabel: group.DisplayName,
            badge: (
              <Avatar
                name={group.DisplayName}
                shape="square"
                image={image}
                color="colorful"
                size={20}
              />
            ),
          };
        }),
      type: "combobox",
      isSingleSelect: false,
      isOverflowItemsFrom10: true,
      isLogValuesInTelemetry: false,
      width: "wide",
    };
  }

  DisableOptions(_selectedFilterValues: Record<FilterName, string[]>): void {
    return;
  }
}

export class RoleFilterWrapper extends BaseFilterWrapper {
  private roleValueMap = new Map<string, Set<string>>();
  private isNested = true;

  constructor({
    isDisabled,
    isNested,
  }: BaseFilterWrapperConstructorProps & { isNested?: boolean }) {
    super({ isDisabled });
    this.isNested = !!isNested;
    this.registerOptionListener((_options: FilterOption[]) => {
      const fromOrganization = Array.from(
        this.roleValueMap.get("FromOrganization") || new Set()
      ) as string[];
      const fromMicrosoft = Array.from(
        this.roleValueMap.get("FromMicrosoft") || new Set()
      ) as string[];
      this.submenuGroups = [
        ...(fromOrganization.length > 0
          ? [
              {
                groupLabel: "FromOrganization" as OrganizationType,
                nestedOptions: constructNestedOptions(
                  fromOrganization.map((value) => ({
                    group: "Role",
                    value: value,
                  }))
                ) as NestedFilter[],
              },
            ]
          : []),
        ...(fromMicrosoft.length > 0
          ? [
              {
                groupLabel: "FromMicrosoft" as OrganizationType,
                nestedOptions: constructNestedOptions(
                  fromMicrosoft.map((value) => ({
                    group: "Role",
                    value: value,
                  }))
                ) as NestedFilter[],
              },
            ]
          : []),
      ] as SubMenuGroup[];
    });
  }
  ParsePrompt(prompt: Prompt): void {
    if (prompt.Metadata) {
      if (isTenantPrompt(prompt)) {
        prompt.Metadata.forEach((mD) => {
          if (mD.Name === "Role") {
            if (!this.roleValueMap.has("FromOrganization")) {
              this.roleValueMap.set("FromOrganization", new Set<string>());
            }
            this.roleValueMap.get("FromOrganization")?.add(mD.Value);
          }
        });
      } else {
        prompt.Metadata.forEach((mD) => {
          if (mD.Name === "Role") {
            if (!this.roleValueMap.has("FromMicrosoft")) {
              this.roleValueMap.set("FromMicrosoft", new Set<string>());
            }
            this.roleValueMap.get("FromMicrosoft")?.add(mD.Value);
          }
        });
      }
      this.Options = [
        ...[...this.roleValueMap.entries()].flatMap(([key, valueSet]) => {
          return [...valueSet].map((value) => {
            return {
              value: value,
              group: key,
            } as FilterOption;
          }) as FilterOption[];
        }),
      ];
    }
  }
  GetFilter(selectedFilterValues?: Record<FilterName, string[]>): Filter {
    let options = this.Options;
    let submenuGroups = this.submenuGroups || [];

    if (selectedFilterValues) {
      options = options.map((option) => {
        return RoleFilterWrapper.DisableMenuOptions(
          option,
          selectedFilterValues
        );
      });
      submenuGroups = submenuGroups.map((group) =>
        disableSubMenuOptions(group, selectedFilterValues)
      );
    }
    return {
      key: RoleKey,
      options: options,
      type: this.isNested ? "nested" : "dropdown",
      isLogValuesInTelemetry: true,
      submenuGroups: submenuGroups,
    };
  }
  DisableOptions(_selectedFilterValues: Record<FilterName, string[]>): void {
    return;
  }
  static DisableMenuOptions = (
    roleFilterOption: FilterOption,
    selectedValues: Record<string, string[]>
  ): FilterOption => {
    const modifiedOption = { ...roleFilterOption };

    const jobTypeValues = selectedValues[JobTypeKey] || [];
    const roleFilterValues = selectedValues[RoleKey] || [];
    const createdByValues = selectedValues[CreatedByKey] || [];

    const jobTypeGroups = new Set<string>();
    jobTypeValues.forEach((jobTypeFilterValue) => {
      const jobTypeFilterValueSplit = jobTypeFilterValue.split(
        FILTER_CONCATENATE_TOKEN
      );
      const jobTypeFilterValueGroup = jobTypeFilterValueSplit[1];
      jobTypeGroups.add(jobTypeFilterValueGroup);
    });

    const roleGroups = new Set<string>();
    roleFilterValues.forEach((roleValue) => {
      const roleValueSplit = roleValue.split(FILTER_CONCATENATE_TOKEN);
      const roleValueGroup = roleValueSplit[1];
      roleGroups.add(roleValueGroup);
    });

    if (modifiedOption.group === "FromOrganization") {
      modifiedOption.disabled =
        (createdByValues.length > 0 &&
          !createdByValues.includes("CreatedByTenant" as CreatedByCategory) &&
          !createdByValues.includes("CreatedByAll" as CreatedByCategory)) ||
        (jobTypeGroups.size > 0 && !jobTypeGroups.has("FromOrganization")) ||
        (roleGroups.size > 0 && !roleGroups.has("FromOrganization"));
    } else if (modifiedOption.group === "FromMicrosoft") {
      modifiedOption.disabled =
        (createdByValues.length > 0 &&
          !createdByValues.includes(
            "CreatedByMicrosoft" as CreatedByCategory
          ) &&
          !createdByValues.includes("CreatedByAll" as CreatedByCategory)) ||
        (jobTypeGroups.size > 0 && !jobTypeGroups.has("FromMicrosoft")) ||
        (roleGroups.size > 0 && !roleGroups.has("FromMicrosoft"));
    }

    return modifiedOption;
  };
}

type SeparatedJobTypeFilterWrapperConstructorProps =
  BaseFilterWrapperConstructorProps & {
    metadataName: string;
    filterKey: FilterName;
  };

export class SeparatedJobTypeFilterWrapper extends BaseFilterWrapper {
  private optionValues: Set<string> = new Set<string>();
  private valueCounts: Record<string, number> = {};
  private metadataName: string;
  private filterKey: FilterName;
  constructor({
    isDisabled,
    metadataName,
    filterKey,
  }: SeparatedJobTypeFilterWrapperConstructorProps) {
    super({ isDisabled });
    this.metadataName = metadataName;
    this.filterKey = filterKey;
  }
  ParsePrompt(prompt: Prompt): void {
    if (isTenantPrompt(prompt) && prompt.Metadata) {
      prompt.Metadata.forEach((mD) => {
        if (mD.Name === this.metadataName) {
          this.optionValues.add(mD.Value);
        }

        if (this.valueCounts[mD.Value]) {
          this.valueCounts[mD.Value] += 1;
        } else {
          this.valueCounts[mD.Value] = 1;
        }
      });
    }
  }
  GetFilter(
    selectedFilterValues?: Record<FilterName, string[]>,
    prompts?: Prompt[]
  ): Filter {
    return {
      key: this.filterKey,
      options: [...new Set(this.optionValues)]
        .map((x) => {
          return { value: x };
        })
        .filter((option) => {
          if (selectedFilterValues) {
            return (
              selectedFilterValues[this.filterKey] &&
              selectedFilterValues[this.filterKey]?.includes(option.value)
            );
          }
          return prompts
            ? this.valueCounts[option.value] !== prompts.length
            : true;
        }),
      type: "dropdown",
      isLogValuesInTelemetry: true,
    };
  }

  DisableOptions(_selectedFilterValues: Record<FilterName, string[]>): void {
    return;
  }
}
